/**
 * Generate a value for Adobe Analytics, Google UA, etc consisting of experiment id, audience id, and holdback status
 *
 * @module
 */

var _ = require('optimizely-client-fn');
var di = require('core/di');

var DELIMITER = ':';
var EVERYONE_ELSE_AUDIENCE_STR = 'everyone_else';
var HOLDBACK_STR = 'holdback';
var TREATMENT_STR = 'treatment';
var DEFAULT_STR = '';
var Logger = require('core/utils/logger');
var Policy = require('core/utils/policy');
var BACKFILLED_LAYER_INTEGRATION_STRING = __BACKFILLED_LAYER_INTEGRATION_STRING__;
/**
 * Taken from the existing repo to clean string names here:
 * https://github.com/optimizely/optimizely
 * /blob/3e416a5113fecc7f88cda2de67ef2f0993cd3573/src/www/js/client/integrator.js#L310
 *
 * @param {string} input
 * @private
 */
function cleanString(input:string) {
  return input.replace(/[^a-zA-Z0-9\.\~\!\*\(\)\']+/g, '_');
}

/**
 * Given an audience condition list, return whether or not it is complex.
 *
 * I.e., a complex conditionList looks like this (starts with an and/or/not operator):
 *
 *    [ 'or', '89008', [ 'and', '89010', '107027', [ 'or', '89008', '89012'] ] ]
 *
 *    Or this:
 *
 *    [ 'or', '89008', '89010', '107027', '89012' ]
 *
 * A "simple" condition list looks like this:
 *
 *    [ '89008', '89010', '107027' ]
 *
 * @private
 */
function isConditionListComplex(conditionList) {
  return !_.isEmpty(conditionList) && _.includes(['and', 'or', 'not'], conditionList[0]);
}

/**
 * An array of audience ID strings
 *    --OR--
 * An array that represents a condition_list structure
 *   I.e.  [ 'or', '89008', [ 'and', '89010', '107027' ] ]
 * @typedef {Array} Audiences
 */

/**
 * @param {module:stores/audience_data} AudienceDataStore
 * @param {module:plugins/helpers/generate_analytics_value~Audiences} audienceIds
 * @return {string} concatenation of all the audience names(fallback to id) or EVERYONE_ELSE_AUDIENCE_STR
 * @private
 */
function concatenatedAudienceString(AudienceStore, audienceIds:import('core/plugins/helpers/generate_analytics_value').Audiences): string {
  var audienceNameStr = '';

  if (!_.isEmpty(audienceIds)) {
    audienceNameStr = _.reduce(audienceIds, function(accumulator, audienceId) {
      var audience = AudienceStore.get(audienceId);
      if (!audience) {
        // This shouldn't happen in theory, but add a safeguard in case.
        return accumulator;
      }
      return accumulator + cleanString(audience.name ? audience.name : audience.id) + ',';
    }, '');
    audienceNameStr = audienceNameStr.slice(0, -1);
  } else {
    audienceNameStr = EVERYONE_ELSE_AUDIENCE_STR;
  }

  return audienceNameStr;
}

/**
 * @typedef {Object} FormattedNamesAndIds
 * @property {Object} names of entity type to entity name string
 * @property {Object} idStrings of entity type to entity id string
 * {
 *   names: {
 *     layer: 'layer_1',
 *     experiment: 'experiment_1',
 *     variation: 'variation_1',
 *   },
 *   idStrings: {
 *     layer: '(1234)',
 *     experiment: '(5678)',
 *     variation: '(9876)',
 *   }
 * }
 */

/**
 * Helper function to format names and ids for forming analytics strings/objects
 * Layer and variation name is formatted as is or passed through cleanString
 * If experiment doesn't have a name, experiment's audience names are concatenated
 *
 * @param {module:stores/audience_data} AudienceDataStore
 * @param {Layer} layer
 * @param {Experiment} experiment
 * @param {Variation} variation
 * @param {boolean} shouldCleanString Whether or not to format any string as lowercase with spaces removed
 * @return {module:plugins/helpers/generate_analytics_value~FormattedNamesAndIds}
 */
exports.formatNamesAndIdsForAnalytics = function formatNamesAndIdsForAnalytics(layer:Layer, experiment:Experiment, variation:Variation, shouldCleanString:boolean): import('core/plugins/helpers/generate_analytics_value').FormattedNamesAndIds {
  var names = {
    layer: layer.name || DEFAULT_STR,
    experiment: experiment.name || DEFAULT_STR,
    variation: variation.name || DEFAULT_STR,
  };

  if (shouldCleanString) {
    names = _.mapValues(names, cleanString);
  }
  // TODO(kelly): remove name construction based on audience names - CJS-1294
  var isIntegrationStringVersionOneOrEquivalent = BACKFILLED_LAYER_INTEGRATION_STRING ? layer.integrationStringVersion === 1 : !layer.integrationStringVersion || layer.integrationStringVersion === 1;
  if (names.experiment === DEFAULT_STR && isIntegrationStringVersionOneOrEquivalent) {
    if (!isConditionListComplex(experiment.audienceIds)) {
      // TODO(kelly): remove dependency on Audience store - CJS-1222
      var AudienceStore = di.get('stores/audience_data');
      // Our audienceIds list is simple (like [‘234’, ‘456’]),
      // so just concatenate the audience names
      names.experiment = concatenatedAudienceString(AudienceStore, experiment.audienceIds);
    } else {
      // Bummer, we dont have an experiment name and our audience conditions are complex.
      // Fallback to simply 'Exp', resulting in users seeing a value like "Exp(123)"
      names.experiment = 'Exp';
    }
  }

  var idStrings = {
    layer: '(' + cleanString(layer.id) + ')',
    experiment: '(' + cleanString(experiment.id) + ')',
    variation: '(' + cleanString(variation.id) + ')',
  };

  return {
    names: names,
    idStrings: idStrings,
  };
};

/**
 * Helper function to concatenate entity name with its id, truncating name if necessary
 *
 * @param {string} name
 * @param {string} idString
 * @param {number} maxLength the maximum length of the string to return
 * @returns {string}
 * @private
 */
exports.combineAndTruncateIdAndName = function(name:string, idString:string, maxLength:number): string {
  var lengthAvailableForName = maxLength - idString.length;

  if (lengthAvailableForName < 0) {
    Logger.warn('maxLength must be at least long enough to fit the entity ID, which is length' + idString.length + '. Defaulting to only use entity ID as name.');
    name = DEFAULT_STR;
  }

  if (name === DEFAULT_STR) {
    return idString;
  } else {
    if (name.length > lengthAvailableForName) {
      var nameOutputSize = Math.min(name.length, lengthAvailableForName);
      name = name.substring(0, nameOutputSize);
      // If the name has been truncated, don't include a space between the
      // name and id to save space
      return name + idString;
    }

    return name + ' ' + idString;
  }
};

/**
 * Generate a value string from layer, experiment, variation, and holdback status
 * Logic is forked on integrationStringVersion field on layer
 *
 * @param {Layer} layer
 * @param {Experiment} experiment
 * @param {Variation} variation
 * @param {boolean} isHoldback Whether or not the visitor is in the holdback
 * @param {number} strLength the maximum length of the string to return
 * @param {boolean} shouldCleanString Whether to run the string through cleanString
 * @returns {string|undefined} value to send to analytics provider, if applicable
 */
exports.generateAnalyticsString = function generateAnalyticsString(layer:Layer, experiment:Experiment, variation:Variation, isHoldback:boolean, strLength:number, shouldCleanString:boolean): string|undefined {
  if (layer.integrationStringVersion && layer.integrationStringVersion === 2) {
    return generateAnalyticsStringVersion2(layer, experiment, variation, isHoldback, strLength, shouldCleanString);
  }
  return generateAnalyticsStringVersion1(layer, experiment, variation, isHoldback, strLength, shouldCleanString);
};

/**
 * Version 2 of generateAnalyticsString logic. String is made of:
 * 1. Layer name, if not a single-experiment layer, or empty string if layer has no name.
 * 2. Layer ID, if not a single-experiment layer
 * 3. LayerExperiment name, or empty string if experiment has no name
 * 4. LayerExperiment ID
 * 5. Variation name, or empty string if variation has no name
 * 6. Variation ID
 * 7. Visitor holdback status
 *
 * The names are truncated to keep the string.length <= strLength
 * If the strLength does not allow enough room for IDs, an error is thrown.
 *
 * @param {Layer} layer
 * @param {Experiment} experiment
 * @param {Variation} variation
 * @param {boolean} isHoldback Whether or not the visitor is in the holdback
 * @param {number} strLength the maximum length of the string to return
 * @param {boolean} shouldCleanString Whether to run the string through cleanString
 * @returns {string|undefined} value to send to analytics provider, if applicable
 * @private
 */
function generateAnalyticsStringVersion2(layer:Layer, experiment:Experiment, variation:Variation, isHoldback:boolean, strLength:number, shouldCleanString:boolean): string|undefined {
  // Indicate that "tracking" shouldn't happen if user is not bucketed due to traffic allocation
  // Holdback for AB tests is used as a proxy for traffic allocation, which will be addressed in WEB-911
  // Users in the holdback for an AB test are not a meaningful segment to log to analytics integrations
  if (Policy.isSingleExperimentPolicy(layer.policy) && isHoldback) {
    return;
  }
  var includeHoldbackString = !Policy.isSingleExperimentPolicy(layer.policy) && isHoldback;
  var formattedNamesAndIds = exports.formatNamesAndIdsForAnalytics(layer, experiment, variation, shouldCleanString);

  var names = [formattedNamesAndIds.names.experiment, formattedNamesAndIds.names.variation];
  var idStrings = [formattedNamesAndIds.idStrings.experiment, formattedNamesAndIds.idStrings.variation];
  if (!Policy.isSingleExperimentPolicy(layer.policy)) {
    names.unshift(formattedNamesAndIds.names.layer);
    idStrings.unshift(formattedNamesAndIds.idStrings.layer);
  }

  var numBytesForIds = _.reduce(
    idStrings,
    function(accumulator, idStr) {
      return accumulator + idStr.length;
    },
    0
  );

  // Delimiter will go in between each name/id pair
  var numberOfDelimiters = (names.length - 1) + (includeHoldbackString ? 1 : 0);
  var delimiterSpace = numberOfDelimiters * DELIMITER.length;
  var minimumStrLength = numBytesForIds + delimiterSpace;

  if (includeHoldbackString) {
    minimumStrLength += HOLDBACK_STR.length;
  }

  if (minimumStrLength > strLength) {
    throw new Error('The analytics string size is too low to send the entity IDs.');
  }

  var numBytesForNames = strLength - minimumStrLength;

  var numUnallocatedNames = names.length;
  // Iterate over names in reverse so that layer name has size precedence
  var truncatedNames = [];
  for (var i = names.length - 1; i >= 0; i--) {
    var name = names[i];
    var nameSize = Math.min(name.length, Math.floor(numBytesForNames / numUnallocatedNames));
    numBytesForNames -= nameSize;
    numUnallocatedNames--;
    truncatedNames.unshift(name.substring(0, nameSize));
  }

  var analyticsData = _.map(truncatedNames, function(truncatedName, index) {
    return truncatedName + idStrings[index];
  });

  if (includeHoldbackString) {
    analyticsData.push(HOLDBACK_STR);
  }

  return analyticsData.join(DELIMITER);
}

/**
 * Legacy version of generateAnalyticsString logic. String is made of:
 * 1. Layer name, or empty string if layer represents a single experiment or has no name for another reason
 * 2. Layer ID, if layer does not represent a single experiment
 * 3. LayerExperiment name, or concatenation of audience names/IDs if experiment has no name
 * 4. LayerExperiment ID
 * 5. Variation name, or empty string if variation has no name
 * 6. Variation ID
 * 7. Visitor holdback status
 *
 * The names are truncated to keep the string.length <= strLength
 * If the strLength does not allow enough room for IDs, an error is thrown.
 *
 * @param {Layer} layer
 * @param {Experiment} experiment
 * @param {Variation} variation
 * @param {boolean} isHoldback Whether or not the visitor is in the holdback
 * @param {number} strLength the maximum length of the string to return
 * @param {boolean} shouldCleanString Whether to run the string through cleanString
 * @returns {string} value to send to analytics provider
 * @private
 */
function generateAnalyticsStringVersion1(layer:Layer, experiment:Experiment, variation:Variation, isHoldback:boolean, strLength:number, shouldCleanString:boolean): string {
  var holdbackStr = isHoldback ? HOLDBACK_STR : TREATMENT_STR;
  var delimiterSpace = 3 * DELIMITER.length;
  var formattedNamesAndIds = exports.formatNamesAndIdsForAnalytics(layer, experiment, variation, shouldCleanString);
  var names = formattedNamesAndIds.names;
  var idStrings = formattedNamesAndIds.idStrings;

  var numBytesForIds = _.reduce(
    idStrings,
    function(accumulator, idStr) {
      return accumulator + idStr.length;
    },
    0
  );
  if (numBytesForIds + delimiterSpace + holdbackStr.length > strLength) {
    throw new Error('The analytics string size is too low to send the campaign, experiment, and variation IDs.');
  }

  var numBytesForNames = strLength - numBytesForIds - delimiterSpace - holdbackStr.length;

  // There's 3 names we need to fit into numBytesForNames.
  // The variation name gets Math.min(it's length, one third of the available space)
  // The experiment name gets Math.min(it's length, half of the remaining space)
  // The layer name gets the rest of the space
  var nameSizes = {} as any;
  nameSizes.variation = Math.min(names.variation.length, Math.floor(numBytesForNames / 3));
  numBytesForNames -= nameSizes.variation;
  nameSizes.experiment = Math.min(names.experiment.length, Math.floor(numBytesForNames / 2));
  numBytesForNames -= nameSizes.experiment;
  nameSizes.layer = numBytesForNames;

  var nameStrings = {} as any;
  _.each(names, function(name, key) {
    nameStrings[key] = name.substring(0, nameSizes[key]);
  });

  var analyticsData = [];

  // Only include the layer name and id if NOT a single experiment
  if (!Policy.isSingleExperimentPolicy(layer.policy)) {
    analyticsData.push(nameStrings.layer + idStrings.layer);
  }
  analyticsData = analyticsData.concat([
    nameStrings.experiment + idStrings.experiment,
    nameStrings.variation + idStrings.variation,
    holdbackStr
  ]);

  return analyticsData.join(DELIMITER);
}
